import type { LucideIcon } from 'lucide-react';
import {
  Blinds,
  Cable,
  Fan,
  Lamp,
  LampCeiling,
  LampFloor,
  LampWallUp,
  Lightbulb,
  Plug,
  Power,
  Sparkles,
  Sun,
} from 'lucide-react';

/**
 * Glyphs a technician can assign to a load, so the room controls page mirrors
 * what is physically installed rather than what its type implies.
 */
export const LOAD_ICON = {
  LIGHTBULB: 'lightbulb',
  CEILING: 'ceiling',
  CHANDELIER: 'chandelier',
  WALL_LIGHT: 'wall_light',
  FLOOR_LAMP: 'floor_lamp',
  TABLE_LAMP: 'table_lamp',
  SPOT: 'spot',
  STRIP: 'strip',
  BLIND: 'blind',
  FAN: 'fan',
  OUTLET: 'outlet',
} as const;

export type LoadIconKey = (typeof LOAD_ICON)[keyof typeof LOAD_ICON];

export const LOAD_ICON_MAP = {
  [LOAD_ICON.LIGHTBULB]: Lightbulb,
  [LOAD_ICON.CEILING]: LampCeiling,
  [LOAD_ICON.CHANDELIER]: Sparkles,
  [LOAD_ICON.WALL_LIGHT]: LampWallUp,
  [LOAD_ICON.FLOOR_LAMP]: LampFloor,
  [LOAD_ICON.TABLE_LAMP]: Lamp,
  [LOAD_ICON.SPOT]: Sun,
  [LOAD_ICON.STRIP]: Cable,
  [LOAD_ICON.BLIND]: Blinds,
  [LOAD_ICON.FAN]: Fan,
  [LOAD_ICON.OUTLET]: Plug,
} as const satisfies Record<LoadIconKey, LucideIcon>;

/** Palette order shown in the load editor. */
export const LOAD_ICON_ORDER = [
  LOAD_ICON.LIGHTBULB,
  LOAD_ICON.CEILING,
  LOAD_ICON.CHANDELIER,
  LOAD_ICON.WALL_LIGHT,
  LOAD_ICON.FLOOR_LAMP,
  LOAD_ICON.TABLE_LAMP,
  LOAD_ICON.SPOT,
  LOAD_ICON.STRIP,
  LOAD_ICON.BLIND,
  LOAD_ICON.FAN,
  LOAD_ICON.OUTLET,
] as const satisfies readonly LoadIconKey[];

/** Fallback glyph used when a load's stored icon is not recognised. */
export const FALLBACK_LOAD_ICON: LucideIcon = Power;

/** Runtime guard for values arriving from storage. */
export const isLoadIconKey = (value: string): value is LoadIconKey =>
  LOAD_ICON_ORDER.some((key) => key === value);

/** Glyph for a stored icon key, falling back rather than rendering nothing. */
export const loadIconFor = (value: string): LucideIcon =>
  isLoadIconKey(value) ? LOAD_ICON_MAP[value] : FALLBACK_LOAD_ICON;
